// src/app/core/services/auth.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';

export interface User {
    id: number;
    name: string;
    email: string;
    google_id?: string;
    avatar?: string;
    status: string;
    created_at: string;
    updated_at: string;
}

export interface RegisterRequest {
    name: string;
    email: string;
    password: string;
}

export interface AuthResponse {
    accessToken: string;
    tokenType: string;
    expires_in: number;
    user: User;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private apiUrl = environment.urlApi;
    private tokenKey = 'access_token';
    private userKey = 'user_data';
    
    private currentUserSubject = new BehaviorSubject<User | null>(null);
    public currentUser$ = this.currentUserSubject.asObservable();
    
    private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

    constructor(
        private http: HttpClient,
        private router: Router
    ) {
        this.loadStoredData();
    }

    /**
     * Inscription
     */
    register(data: RegisterRequest): Observable<AuthResponse> {
        return this.http.post<AuthResponse>(`${this.apiUrl}/auth/register`, data)
            .pipe(
                tap(response => this.handleAuthResponse(response)),
                catchError(this.handleError)
            );
    }

    /**
     * Authentification avec Google
     */
    authenticateWithGoogle(googleToken: string): Observable<AuthResponse> {
        return this.http.post<AuthResponse>(
            `${this.apiUrl}/auth/google`,
            { google_token: googleToken }
        ).pipe(
            tap(response => this.handleAuthResponse(response)),
            catchError(this.handleError)
        );
    }

    /**
     * Gérer la réponse d'authentification
     */
    private handleAuthResponse(response: AuthResponse): void {
        if (response.accessToken && response.user) {
            localStorage.setItem(this.tokenKey, response.accessToken);
            localStorage.setItem(this.userKey, JSON.stringify(response.user));
            
            this.currentUserSubject.next(response.user);
            this.isAuthenticatedSubject.next(true);
        }
    }

    /**
     * Charger les données stockées
     */
    private loadStoredData(): void {
        const token = this.getToken();
        const user = this.getUser();
        
        if (token && user) {
            this.currentUserSubject.next(user);
            this.isAuthenticatedSubject.next(true);
        }
    }

    /**
     * Obtenir le token
     */
    getToken(): string | null {
        return localStorage.getItem(this.tokenKey);
    }

    /**
     * Obtenir l'utilisateur stocké
     */
    getUser(): User | null {
        const userData = localStorage.getItem(this.userKey);
        if (userData) {
            try {
                return JSON.parse(userData);
            } catch {
                return null;
            }
        }
        return null;
    }

    /**
     * Vérifier si l'utilisateur est authentifié
     */
    isAuthenticated(): boolean {
        return this.isAuthenticatedSubject.value && !!this.getToken();
    }

    /**
     * Déconnexion
     */
    logout(): void {
        localStorage.removeItem(this.tokenKey);
        localStorage.removeItem(this.userKey);
        
        this.currentUserSubject.next(null);
        this.isAuthenticatedSubject.next(false);
        
        this.router.navigate(['/sign-in']);
    }

    /**
     * Gestion des erreurs
     */
    private handleError(error: any): Observable<never> {
        console.error('Auth Error:', error);
        return throwError(() => error);
    }
}